// Phase JA.3 — No-Bet Report.
//
// Today's suppression breakdown by rule_id. Frames "we said no"
// moments as STRIKZA's quality discipline — promotion, not restriction.
//
// Static dictionary lives here (not in DB) — copy is product surface.

import { Eyebrow, HairlineRow, Pill, Stat } from "../super-acca";
import type { SuppressionBreakdown } from "../../free-tier/useDailyAccaIntel.ts";

export const RULE_COPY: Record<string, { title: string; body: string }> = {
  S1: {
    title: "S1 — Liquidity below threshold",
    body: "Market is too thin to publish with confidence.",
  },
  S2: {
    title: "S2 — Conviction split between agents",
    body: "The three-tier panel disagreed; we don't ship a divided read.",
  },
  S3: {
    title: "S3 — Kickoff too soon for confident read",
    body: "Information window closed before we finished verification.",
  },
  S4: {
    title: "S4 — Sharp money moved against the read",
    body: "Late market movement contradicted our model.",
  },
  S5: {
    title: "S5 — CLV edge collapsed pre-publish",
    body: "Closing-line value erosion took the price below our floor.",
  },
  S6: {
    title: "S6 — Correlation overload",
    body: "Too many candidates from the same fixture or league cluster.",
  },
  N02: {
    title: "N02 — Founder's Pick gate",
    body: "Best available candidate didn't clear the Founder's Pick bar.",
  },
  "Q-GATE": {
    title: "Q-GATE — Final quality gate",
    body: "Last-pass discipline check before the day's menu publishes.",
  },
};

export interface NoBetReportProps {
  breakdown: SuppressionBreakdown[];
  totalSuppressed: number;
  loading?: boolean;
}

export function NoBetReport({
  breakdown,
  totalSuppressed,
  loading,
}: NoBetReportProps): JSX.Element {
  const isEmpty = !loading && breakdown.length === 0;
  return (
    <section
      aria-label="No-Bet Report"
      data-testid="no-bet-report"
      style={{
        margin: "0 auto",
        maxWidth: 1180,
        padding: "12px 48px 32px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 14,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <Eyebrow>No-Bet Report · Today</Eyebrow>
        <Stat label="Total Rejected" value={String(totalSuppressed)} sub="quality discipline" />
      </div>

      {loading ? (
        <div
          style={{
            padding: 14,
            border: "1px solid var(--sa-hairline)",
            borderRadius: 4,
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
            letterSpacing: "0.22em",
            color: "var(--sa-fg-muted)",
            textTransform: "uppercase",
          }}
        >
          Loading suppression log…
        </div>
      ) : isEmpty ? (
        <div
          style={{
            padding: 14,
            border: "1px solid var(--sa-hairline)",
            borderRadius: 4,
            fontFamily: "var(--sa-font-body)",
            fontSize: 13,
            color: "var(--sa-fg-secondary)",
          }}
        >
          No suppressions today — all candidates passed the gate.
        </div>
      ) : (
        <div
          style={{
            border: "1px solid var(--sa-hairline)",
            borderRadius: 4,
            padding: "0 14px",
          }}
        >
          {breakdown.map((entry, i) => {
            const meta = RULE_COPY[entry.rule_id] ?? {
              title: `${entry.rule_id} — Suppression rule`,
              body: "Quality-discipline gate triggered.",
            };
            return (
              <HairlineRow
                key={entry.rule_id}
                first={i === 0}
                columns="auto 1fr auto"
                padY={14}
              >
                <Pill color="var(--sa-fg-secondary)">{entry.rule_id}</Pill>
                <div>
                  <div
                    style={{
                      fontFamily: "var(--sa-font-body)",
                      fontSize: 13.5,
                      color: "var(--sa-fg-primary)",
                    }}
                  >
                    {meta.title}
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--sa-font-body)",
                      fontSize: 12.5,
                      color: "var(--sa-fg-muted)",
                      marginTop: 4,
                      lineHeight: 1.5,
                    }}
                  >
                    {meta.body}
                  </div>
                </div>
                <Stat label="Count" value={String(entry.count)} />
              </HairlineRow>
            );
          })}
        </div>
      )}
    </section>
  );
}
